/**
 * MED-781: A single Needs Attention rail card. Renders one of six variants
 * (Starts Today / Overdue Attendance / Prep Needed / Session Report Ready /
 * Connect Calendly / Feedback) inside a shared frame.
 *
 * AC16: Only the primary CTA button is interactive. Clicking elsewhere on the
 * card does NOT navigate or trigger anything — the frame uses no onClick or
 * UnstyledButton wrapper.
 */

import { Card, Group, Stack, Text, Button, Box } from '@mantine/core';
import {
  Calendar,
  Clock,
  Users,
  AlertCircle,
  FileText,
  ExternalLink,
  Link2,
  Edit3,
} from 'react-feather';
import type { ReactNode } from 'react';
import type {
  NeedsAttentionItem,
  StartsTodayItem,
  OverdueAttendanceItem,
  PostSessionFeedbackItem,
  PrepNeededItem,
  SessionReportReadyItem,
  FeedbackItem,
  CalendlyBookingBlockedItem,
  MeetingLinkMissingItem,
  Calendly20minUnmappedItem,
} from './needsAttentionTypes';
import { missingLinkText } from '../../../../components/coaches/meetingLinkPrompt';
import { twentyMinuteCardText } from '../../../../components/coaches/calendlyDurations';
import { ParticipantLabelDivergenceIndicator } from '../../../../components/common';

const CARD_WIDTH = 320;

// MED-963 (polish pass): exact values read from the Figma design via the Figma
// MCP (file FckW8tyXnafgNbIaWQDMrd, node 1991:4960), replacing the earlier
// screenshot-eyeballed approximations that QA flagged. The colors are raw fills
// in the design (no bound Figma variables), so they live as scoped constants.

// SESSION START (node 1991:4973): light-blue card, steel-blue countdown pill,
// blue group dot, dark-navy CTA. Note the pill fill (#3b82b0) and the dot
// (#3b82f6) are intentionally DIFFERENT blues in the design.
const STARTS_TODAY_BG = '#ebf2fa'; // light blue card tint
const STARTS_TODAY_BORDER = '#b0cce0'; // light blue 2px border
const STARTS_TODAY_PILL_BG = '#3b82b0'; // steel-blue countdown pill
const STARTS_TODAY_DOT = '#3b82f6'; // blue group dot
const STARTS_TODAY_NAVY = '#203358'; // dark navy CTA
// MDF-244: style-guide status colours so the live and late states read apart
// from the countdown at a glance, not only by their words.
const STARTS_TODAY_LIVE = '#D64545'; // STYLE_GUIDE "Live" status (border)
// White 13px text on #D64545 is 4.4:1, under WCAG AA; this darker red is 5.5:1.
const STARTS_TODAY_LIVE_PILL = '#C53030';
const STARTS_TODAY_LATE = '#E9A23B'; // STYLE_GUIDE "Warning Amber" (current item)
// White on amber is 2.2:1, so the late pill uses Text Primary (8.0:1).
const STARTS_TODAY_LATE_TEXT = '#1A1A1A';
// Under this many minutes past start, the card reads "Starting now"; after it,
// "N min late".
const STARTING_NOW_GRACE_MIN = 2;

// OVERDUE (node 1991:5002): a single saturated red (#dc2626) drives the badge
// text, the 1px card border, AND the CTA fill; the badge sits on a #fee2e2
// tint. (The prior pass mis-read the border as a light pink.)
const OVERDUE_RED = '#dc2626';
const OVERDUE_BADGE_BG = '#fee2e2';

// PREP NEEDED (node 1991:5024): very pale cream badge + amber-600 text/dot.
const PREP_BADGE_BG = '#fffdf5';
const PREP_BADGE_TEXT = '#d97706';
const PREP_DOT = '#f59e0b';
const URGENCY_BORDER = 'var(--mantine-color-warningAmber-6)';

// REVIEW (node 1991:5053): neutral gray badge.
const REVIEW_BADGE_BG = '#f3f4f6';
const REVIEW_BADGE_TEXT = '#6b7280';

// Shared neutral outline CTA (PREP / REVIEW "Continue Prep" / "Open Report").
// Mantine's `default` variant gets the white fill + hover for free; these pin
// the border and text to the design's exact grays (its defaults run darker).
const NEUTRAL_BTN_STYLES = {
  root: { borderColor: '#e5e7eb', color: '#374151' },
} as const;

// White cards use a 12px corner radius; the SESSION START card uses 14px.
const CARD_RADIUS = 12;
const STARTS_TODAY_RADIUS = 14;

interface CardFrameProps {
  cohortName: string;
  pill: ReactNode;
  body: ReactNode;
  cta: ReactNode;
  groupLabel?: ReactNode;
  background?: string;
  borderColor?: string;
  borderWidth?: number;
  radius?: number;
}

function CardFrame({
  cohortName,
  pill,
  body,
  cta,
  groupLabel,
  background,
  borderColor,
  borderWidth = 1,
  radius = CARD_RADIUS,
}: CardFrameProps) {
  return (
    <Card
      withBorder={!borderColor}
      padding="md"
      radius={radius}
      style={{
        flex: `0 0 ${CARD_WIDTH}px`,
        background,
        border: borderColor ? `${borderWidth}px solid ${borderColor}` : undefined,
        scrollSnapAlign: 'start',
      }}
    >
      <Stack justify="space-between" gap="sm" h="100%">
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
            <Box style={{ flexShrink: 0 }}>{pill}</Box>
            <Text size="xs" c="dimmed" ta="right" lineClamp={1} style={{ flexShrink: 1, minWidth: 0 }}>
              {cohortName}
            </Text>
          </Group>
          {body}
        </Stack>
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
          <Box style={{ minWidth: 0 }}>{groupLabel}</Box>
          <Box style={{ flexShrink: 0 }}>{cta}</Box>
        </Group>
      </Stack>
    </Card>
  );
}

// Status pill / badge.
//
// MED-963: the design uses TWO treatments here, so the sizing is parametrized:
//   • Status chips (Overdue / Prep Needed / Review): small 10px UPPERCASE text
//     on a lightly-rounded 4px rectangle — the defaults below.
//   • SESSION START countdown ("Starts in X min"): a larger 13px sentence-case
//     fully-rounded pill (radius 20). It opts out via transform="none" plus the
//     fontSize / radius / px / letterSpacing overrides.
function Pill({
  label,
  color,
  background,
  icon,
  transform = 'uppercase',
  fontSize = 10,
  radius = 4,
  px = 8,
  letterSpacing = 0.3,
}: {
  label: string;
  color: string;
  background: string;
  icon?: ReactNode;
  transform?: 'uppercase' | 'none';
  fontSize?: number;
  radius?: number;
  px?: number;
  letterSpacing?: number;
}) {
  return (
    <Group
      gap={4}
      align="center"
      px={px}
      py={3}
      style={{
        background,
        color,
        borderRadius: radius,
        fontSize,
        fontWeight: 700,
        letterSpacing,
        textTransform: transform,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      <span>{label}</span>
    </Group>
  );
}

function GroupDot({ label, color }: { label: string; color: string }) {
  return (
    <Group gap={6} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
      <Box
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      <Text size="xs" c="dimmed" lineClamp={1}>
        {label}
      </Text>
    </Group>
  );
}

function MetadataLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Group gap={6} align="center" wrap="nowrap">
      <Box c="dimmed" style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {children}
      </Text>
    </Group>
  );
}

function formatStartsTodayTime(scheduledStart: string): string {
  const date = new Date(scheduledStart);
  const dayLabel = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Today, ${dayLabel} · ${time}`;
}

function formatScheduledTime(scheduledStart: string): string {
  const date = new Date(scheduledStart);
  const dayLabel = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${dayLabel} · ${time}`;
}

function daysFromNow(iso: string): number {
  const target = new Date(iso);
  const now = new Date();
  const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.max(0, Math.round((targetMidnight - nowMidnight) / (24 * 60 * 60 * 1000)));
}

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

function minutesUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (60 * 1000)));
}

// ---------------------------------------------------------------------------
// Variant: Starts Today (AC9)
// ---------------------------------------------------------------------------

function StartsTodayCardBody({
  item,
  onCta,
}: {
  item: StartsTodayItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const minutes = minutesUntil(item.scheduledStart);
  const isImminent = minutes <= 60;
  // MDF-244: the API keeps a scheduled session listed after its start time
  // until its estimated end. Past the start, invite the coach to start it
  // instead of showing a frozen "Starts in 0 min".
  // The rail re-renders every minute (useNeedsAttentionItems), so the late
  // count stays current.
  const isInProgress = item.status === 'in_progress';
  const msPastStart = Date.now() - new Date(item.scheduledStart).getTime();
  const isReadyToStart = !isInProgress && msPastStart >= 0;
  const minutesLate = Math.floor(msPastStart / (60 * 1000));
  const pillLabel = isInProgress
    ? 'Live now'
    : isReadyToStart
      ? minutesLate < STARTING_NOW_GRACE_MIN
        ? 'Starting now'
        : `${minutesLate} min late`
      : isImminent
        ? `Starts in ${minutes} min`
        : 'Today';
  const stateColor = isInProgress
    ? STARTS_TODAY_LIVE
    : isReadyToStart
      ? STARTS_TODAY_LATE
      : null;
  const ctaLabel = isInProgress
    ? 'Rejoin Session'
    : isReadyToStart
      ? 'Start Session'
      : 'Prep Session';

  return (
    <CardFrame
      cohortName={item.cohortName}
      background={STARTS_TODAY_BG}
      borderColor={stateColor ?? STARTS_TODAY_BORDER}
      borderWidth={2}
      radius={STARTS_TODAY_RADIUS}
      pill={
        <Pill
          label={pillLabel}
          color={
            isReadyToStart && !isInProgress ? STARTS_TODAY_LATE_TEXT : 'white'
          }
          background={
            isInProgress
              ? STARTS_TODAY_LIVE_PILL
              : (stateColor ?? STARTS_TODAY_PILL_BG)
          }
          transform="none"
          fontSize={13}
          radius={20}
          px={12}
          letterSpacing={0}
        />
      }
      body={
        <Stack gap={6}>
          <Group gap={4} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} lineClamp={2}>
              {item.sessionTitle}
            </Text>
            {/* MED-1147: participants see a different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={item.participantDisplayDiffers}
              participantDisplayName={item.participantDisplayName}
            />
          </Group>
          <MetadataLine icon={<Calendar size={12} />}>
            {formatStartsTodayTime(item.scheduledStart)}
          </MetadataLine>
          <Group gap="md" wrap="nowrap">
            <MetadataLine icon={<Users size={12} />}>
              {item.participantCount} participant
              {item.participantCount === 1 ? '' : 's'}
            </MetadataLine>
            {/* AC9: reflection completion — placeholder until reflection
                data is readily available (per ticket caveat). */}
            <MetadataLine icon={<FileText size={12} />}>
              {`0/${item.participantCount} reflections`}
            </MetadataLine>
          </Group>
        </Stack>
      }
      groupLabel={<GroupDot label={item.groupName} color={STARTS_TODAY_DOT} />}
      cta={
        <Button
          size="xs"
          color={STARTS_TODAY_NAVY}
          variant="filled"
          radius={10}
          onClick={() => onCta(item)}
        >
          {ctaLabel}
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Overdue Attendance (AC10)
// ---------------------------------------------------------------------------

function OverdueAttendanceCardBody({
  item,
  onCta,
}: {
  item: OverdueAttendanceItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const dueLabel =
    item.daysOverdue >= 5
      ? 'Due 5+ days ago'
      : item.daysOverdue === 0
        ? 'Due today'
        : `Due ${item.daysOverdue} day${item.daysOverdue === 1 ? '' : 's'} ago`;

  return (
    <CardFrame
      cohortName={item.cohortName}
      borderColor={OVERDUE_RED}
      borderWidth={1}
      pill={
        <Pill
          label="Overdue"
          color={OVERDUE_RED}
          background={OVERDUE_BADGE_BG}
          icon={<AlertCircle size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Group gap={4} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} lineClamp={2}>
              {item.sessionTitle || 'Mark Attendance'}
            </Text>
            {/* MED-1147: participants see a different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={item.participantDisplayDiffers}
              participantDisplayName={item.participantDisplayName}
            />
          </Group>
          {item.groupName && (
            <MetadataLine icon={<Users size={12} />}>{item.groupName}</MetadataLine>
          )}
          <MetadataLine icon={<Clock size={12} />}>{dueLabel}</MetadataLine>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          color={OVERDUE_RED}
          variant="filled"
          onClick={() => onCta(item)}
          leftSection={<FileText size={12} />}
        >
          Mark Attendance
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Submit Session Feedback (MED-867)
// ---------------------------------------------------------------------------
//
// AC9: deliberately reuses the SAME red "Overdue" treatment as Overdue
// Attendance (OVERDUE_RED / OVERDUE_BADGE_BG) rather than a new color — and is
// differentiated LEXICALLY, since the two cards can sit adjacent for one session
// (AC10). The distinction is carried beyond the CTA button: the title names the
// feedback obligation ("Submit Session Feedback") and the elapsed indicator
// reads "Pending N days" (vs attendance's "Due N days ago"). The red also stays
// distinct from the grape midpoint/endpoint Feedback card — no collision.

function PostSessionFeedbackCardBody({
  item,
  onCta,
}: {
  item: PostSessionFeedbackItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const pendingLabel =
    item.daysPending >= 5
      ? 'Pending 5+ days'
      : item.daysPending === 0
        ? 'Pending today'
        : `Pending ${item.daysPending} day${item.daysPending === 1 ? '' : 's'}`;

  return (
    <CardFrame
      cohortName={item.cohortName}
      borderColor={OVERDUE_RED}
      borderWidth={1}
      pill={
        <Pill
          label="Overdue"
          color={OVERDUE_RED}
          background={OVERDUE_BADGE_BG}
          icon={<AlertCircle size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            Submit Session Feedback
          </Text>
          {item.sessionTitle && (
            <Group gap={4} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
              <MetadataLine icon={<FileText size={12} />}>{item.sessionTitle}</MetadataLine>
              {/* MED-1147: participants see a different label than this canonical topic. */}
              <ParticipantLabelDivergenceIndicator
                differs={item.participantDisplayDiffers}
                participantDisplayName={item.participantDisplayName}
                size={12}
              />
            </Group>
          )}
          <MetadataLine icon={<Clock size={12} />}>{pendingLabel}</MetadataLine>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          color={OVERDUE_RED}
          variant="filled"
          onClick={() => onCta(item)}
          leftSection={<Edit3 size={12} />}
        >
          Submit Feedback
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Prep Needed (AC11 + Comment 3 urgency)
// ---------------------------------------------------------------------------

function PrepNeededCardBody({
  item,
  onCta,
}: {
  item: PrepNeededItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const days = daysFromNow(item.scheduledStart);
  const daysLabel = days === 0 ? 'Today' : days === 1 ? '1 day away' : `${days} days away`;

  const useUrgency = item.isSameDayUrgent;
  // Non-urgent CTA matches the design's neutral gray outline (Mantine's
  // `default` variant: white fill, subtle gray border, dark text) rather than
  // the previous dark outline. Urgent prep stays an amber filled button.
  const ctaProps = useUrgency
    ? ({ color: 'warningAmber', variant: 'filled' as const })
    : ({ variant: 'default' as const, styles: NEUTRAL_BTN_STYLES });

  return (
    <CardFrame
      cohortName={item.cohortName}
      borderColor={useUrgency ? URGENCY_BORDER : undefined}
      borderWidth={useUrgency ? 2 : 1}
      pill={
        <Pill
          label="Prep Needed"
          color={PREP_BADGE_TEXT}
          background={PREP_BADGE_BG}
          icon={<Edit3 size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Group gap={4} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} lineClamp={2}>
              {item.sessionTitle}
            </Text>
            {/* MED-1147: participants see a different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={item.participantDisplayDiffers}
              participantDisplayName={item.participantDisplayName}
            />
          </Group>
          <MetadataLine icon={<Calendar size={12} />}>
            {formatScheduledTime(item.scheduledStart)} · {daysLabel}
          </MetadataLine>
          <Group gap="md" wrap="nowrap">
            <MetadataLine icon={<Edit3 size={12} />}>
              0 prep tasks complete
            </MetadataLine>
            <MetadataLine icon={<Users size={12} />}>
              {item.participantCount} participant
              {item.participantCount === 1 ? '' : 's'}
            </MetadataLine>
          </Group>
        </Stack>
      }
      groupLabel={<GroupDot label={item.groupName} color={PREP_DOT} />}
      cta={
        <Button size="xs" {...ctaProps} onClick={() => onCta(item)}>
          Continue Prep
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Session Report Ready (AC11b)
// ---------------------------------------------------------------------------

function SessionReportReadyCardBody({
  item,
  onCta,
}: {
  item: SessionReportReadyItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const generatedDate = item.attendanceMarkedAt ?? item.scheduledEnd;
  const days = daysSince(generatedDate);
  const freshness =
    days === 0
      ? 'Available today'
      : days === 1
        ? 'Available 1 day'
        : `Available ${days} days`;
  const generatedLabel = new Date(generatedDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <CardFrame
      cohortName={item.cohortName}
      pill={
        <Pill
          label="Review"
          color={REVIEW_BADGE_TEXT}
          background={REVIEW_BADGE_BG}
          icon={<FileText size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Group gap={4} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} lineClamp={2}>
              {item.sessionTitle}
            </Text>
            {/* MED-1147: participants see a different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={item.participantDisplayDiffers}
              participantDisplayName={item.participantDisplayName}
            />
          </Group>
          <MetadataLine icon={<FileText size={12} />}>
            Generated {generatedLabel}
          </MetadataLine>
          <MetadataLine icon={<Clock size={12} />}>{freshness}</MetadataLine>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          variant="default"
          styles={NEUTRAL_BTN_STYLES}
          onClick={() => onCta(item)}
          rightSection={<ExternalLink size={12} />}
        >
          Open Report
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Connect Calendly (AC13)
// ---------------------------------------------------------------------------

function ConnectCalendlyCardBody({
  onCta,
  item,
}: {
  item: NeedsAttentionItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  return (
    <CardFrame
      cohortName=""
      pill={
        <Pill
          label="Setup"
          color="var(--mantine-color-blue-7)"
          background="var(--mantine-color-blue-1)"
          icon={<Link2 size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            Connect Calendly
          </Text>
          <Text size="xs" c="dimmed" lineClamp={3}>
            Connect your Calendly account to enable participant scheduling.
          </Text>
        </Stack>
      }
      cta={
        <Button size="xs" color="blue" variant="filled" onClick={() => onCta(item)}>
          Connect
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Calendly booking blocked (MED-1363)
// ---------------------------------------------------------------------------

function CalendlyBookingBlockedCardBody({
  onCta,
  item,
}: {
  item: CalendlyBookingBlockedItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  return (
    <CardFrame
      cohortName=""
      pill={
        <Pill
          label="Setup"
          color="var(--mantine-color-blue-7)"
          background="var(--mantine-color-blue-1)"
          icon={<Link2 size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            Calendly booking question
          </Text>
          <Text size="xs" c="dimmed" lineClamp={3}>
            {`A required question on your "${item.eventTypeName}" event stops Calendly bookings. Medley sends these bookings as Google invites. Make it optional to book through Calendly.`}
          </Text>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          color="blue"
          variant="filled"
          onClick={() => onCta(item)}
        >
          View Calendly
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Meeting link missing (MED-1365 QA round 6)
// ---------------------------------------------------------------------------

function MeetingLinkMissingCardBody({
  onCta,
  item,
}: {
  item: MeetingLinkMissingItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const text = missingLinkText(item.link, 'coach');
  return (
    <CardFrame
      cohortName=""
      pill={
        <Pill
          label="Setup"
          color="var(--mantine-color-blue-7)"
          background="var(--mantine-color-blue-1)"
          icon={<Link2 size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            {text.title}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={3}>
            {text.body}
          </Text>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          color="blue"
          variant="filled"
          onClick={() => onCta(item)}
        >
          {text.action}
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: 20 min Calendly event type not mapped (MED-1365 QA round 9)
// ---------------------------------------------------------------------------

function Calendly20minUnmappedCardBody({
  onCta,
  item,
}: {
  item: Calendly20minUnmappedItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  // QA round 9: a short step that fits the card. The profile card keeps the
  // full prompt.
  const body = twentyMinuteCardText(item.planStage, item.offTwentyNames);
  return (
    <CardFrame
      cohortName=""
      pill={
        <Pill
          label="Setup"
          color="var(--mantine-color-blue-7)"
          background="var(--mantine-color-blue-1)"
          icon={<Link2 size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            Map a 20 min Calendly event type
          </Text>
          <Text size="xs" c="dimmed" lineClamp={3}>
            {body}
          </Text>
        </Stack>
      }
      cta={
        <Button
          size="xs"
          color="blue"
          variant="filled"
          onClick={() => onCta(item)}
        >
          Edit event type mappings
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Variant: Midpoint / Endpoint Feedback (AC14)
// ---------------------------------------------------------------------------

function FeedbackCardBody({
  item,
  onCta,
}: {
  item: FeedbackItem;
  onCta: (item: NeedsAttentionItem) => void;
}) {
  const label = item.surveyType === 'midpoint' ? 'Midpoint Feedback' : 'Endpoint Feedback';
  return (
    <CardFrame
      cohortName={item.cohortName}
      pill={
        <Pill
          label="Feedback"
          color="var(--mantine-color-grape-7)"
          background="var(--mantine-color-grape-1)"
          icon={<FileText size={10} />}
        />
      }
      body={
        <Stack gap={6}>
          <Text size="sm" fw={700} lineClamp={2}>
            {label}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={2}>
            {item.cohortName}
          </Text>
        </Stack>
      }
      cta={
        <Button size="xs" color="dark" variant="filled" onClick={() => onCta(item)}>
          Complete
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Public NeedsAttentionCard — variant dispatch
// ---------------------------------------------------------------------------

interface NeedsAttentionCardProps {
  item: NeedsAttentionItem;
  onCta: (item: NeedsAttentionItem) => void;
}

export function NeedsAttentionCard({ item, onCta }: NeedsAttentionCardProps) {
  switch (item.type) {
    case 'starts_today':
      return <StartsTodayCardBody item={item} onCta={onCta} />;
    case 'overdue_attendance':
      return <OverdueAttendanceCardBody item={item} onCta={onCta} />;
    case 'post_session_feedback':
      return <PostSessionFeedbackCardBody item={item} onCta={onCta} />;
    case 'prep_needed':
      return <PrepNeededCardBody item={item} onCta={onCta} />;
    case 'session_report_ready':
      return <SessionReportReadyCardBody item={item} onCta={onCta} />;
    case 'connect_calendly':
      return <ConnectCalendlyCardBody item={item} onCta={onCta} />;
    case 'calendly_booking_blocked':
      return <CalendlyBookingBlockedCardBody item={item} onCta={onCta} />;
    case 'meeting_link_missing':
      return <MeetingLinkMissingCardBody item={item} onCta={onCta} />;
    case 'calendly_20min_unmapped':
      return <Calendly20minUnmappedCardBody item={item} onCta={onCta} />;
    case 'feedback':
      return <FeedbackCardBody item={item} onCta={onCta} />;
  }
}
